/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useRef, useEffect } from 'react';
import MeasuremenRegistersObject from 'types/models/widgetsCIM/measurementsRegistersObj';
import { Line } from 'react-chartjs-2';
import { Colors } from 'styles';
import 'chartjs-adapter-date-fns';
import wattsConverterService from 'services/local/WattsConverter';
import { Chart } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { I18n } from 'core';
import { ca, es } from 'date-fns/locale';

Chart.register(zoomPlugin);

interface MeasurementChartPropsI {
  data: MeasuremenRegistersObject;
}
const colorset = {
  activa: '#ff5983',
  capacitiva: '#56ccf2',
  inductiva: '#555555',
  exportada: '#ffa500',
  neta: '#a9330d',
  excedente: '#00a67c',
};

const locales: any = {
  es,
  ca,
};

const MeasurementChart = (props: MeasurementChartPropsI): JSX.Element => {
  const { data } = props;
  const chartRef = useRef<any>(null);
  const TENSION = 0.25;
  const BORDER_WIDTH = 1.25;
  const POINT_RADIUS = 0;
  useEffect(() => {
    if (chartRef) {
      if (chartRef.current) {
        chartRef.current.chartInstance?.resetZoom();
      }
    }
  }, [data]);

  const parsedData = data.datos.map((point) => ({
    x: point.fecha,
    activa: point.activa,
    capacitiva: point.capacitiva,
    inductiva: point.inductiva,
    exportada: point.exportada,
    neta: point.neta,
    excedente: point.excedente,
  }));

  return (
    <Line
      type="line"
      ref={chartRef}
      data={{
        datasets: [
          {
            label: I18n.t('CIM.MeasurementRegistersPage.Activa'),
            data: parsedData,
            borderColor: colorset.activa,
            backgroundColor: colorset.activa,
            borderDashOffset: 0.0,
            borderWidth: BORDER_WIDTH,
            pointRadius: POINT_RADIUS,
            tension: TENSION,
            parsing: {
              yAxisKey: 'activa',
            },
          },
          {
            label: I18n.t('CIM.MeasurementRegistersPage.Capacitiva'),
            data: parsedData,
            borderColor: colorset.capacitiva,
            backgroundColor: colorset.capacitiva,
            borderDashOffset: 0.0,
            borderWidth: BORDER_WIDTH,
            pointRadius: POINT_RADIUS,
            tension: TENSION,
            parsing: {
              yAxisKey: 'capacitiva',
            },
          },
          {
            label: I18n.t('CIM.MeasurementRegistersPage.Inductiva'),
            data: parsedData,
            borderColor: colorset.inductiva,
            backgroundColor: colorset.inductiva,
            borderDashOffset: 0.0,
            borderWidth: BORDER_WIDTH,
            pointRadius: POINT_RADIUS,
            tension: TENSION,
            parsing: {
              yAxisKey: 'inductiva',
            },
          },
          {
            label: I18n.t('CIM.MeasurementRegistersPage.Exportada'),
            data: parsedData,
            borderColor: colorset.exportada,
            backgroundColor: colorset.exportada,
            borderDashOffset: 0.0,
            borderWidth: BORDER_WIDTH,
            pointRadius: POINT_RADIUS,
            tension: TENSION,
            parsing: {
              yAxisKey: 'exportada',
            },
          },
          {
            label: I18n.t('CIM.MeasurementRegistersPage.Neta'),
            data: parsedData,
            borderColor: colorset.neta,
            backgroundColor: colorset.neta,
            borderDashOffset: 0.0,
            borderWidth: BORDER_WIDTH,
            pointRadius: POINT_RADIUS,
            tension: TENSION,
            parsing: {
              yAxisKey: 'neta',
            },
          },
          {
            label: I18n.t('CIM.MeasurementRegistersPage.Excedent'),
            data: parsedData,
            borderColor: colorset.excedente,
            backgroundColor: colorset.excedente,
            borderDashOffset: 0.0,
            borderWidth: BORDER_WIDTH,
            pointRadius: POINT_RADIUS,
            tension: TENSION,
            parsing: {
              yAxisKey: 'excedente',
            },
          },
        ],
      }}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: 10,
        },
        scales: {
          x: {
            color: Colors.COLOR_GRAY_200,
            type: 'time',
            display: true,
            ticks: { maxRotation: 0, minRotation: 0 },
            adapters: {
              date: {
                locale: locales[I18n.currentLocale().substring(0, 2)] || locales.es,
              },
            },
            time: {
              tooltipFormat: 'yyyy/MM/dd HH:mm',
              unit: 'day',
              displayFormats: {
                milisecond: 'HH:mm',
                second: 'HH:mm',
                minute: 'HH:mm',
                hour: 'ddd HH:mm',
                day: 'MMMM dd',
                week: 'MMMM dd',
                month: 'MMMM dd',
                year: 'MMMM dd',
              },
            },
          },
          y: {
            title: {
              display: true,
              text: 'kWh',
            },
            ticks: {
              callback: (value: any) => `${wattsConverterService.toSelectedMagnitudeString(value, 'kWh')} kWh`,
            },
          },
        },
        interaction: {
          intersect: false,
          mode: 'index',
        },

        animation: false,
        plugins: {
          legend: {
            position: 'top',
            align: 'center',
            labels: {
              pointStyle: 'circle',
              usePointStyle: true,
            },
          },
          zoom: {
            pan: {
              enabled: true,
              mode: 'x',
              speed: 100,
              threshold: 100,
            },
            zoom: {
              enabled: true,
              drag: false,
              mode: 'x',
              wheel: {
                enabled: true,
              },
              limits: {
                max: 10,
                min: 0.5,
              },
            },
          },
          tooltip: {
            enabled: true,
            cornerRadius: 8,
            callbacks: {
              label: (tooltipItem: any, dt: any) =>
                `${wattsConverterService.toSelectedMagnitudeString(tooltipItem.parsed.y, 'kWh')} kWh`,
            },
          },
        },
      }}
    />
  );
};

export default React.memo(MeasurementChart);
